// Çoklu seçim (chip listesi). Seçilenleri {value,label} dizisi olarak tutar;
// her seçimde listeye ekler, chip'ten çıkarılabilir.
// İki ekleme modu:
//   - config verilirse: datagrid'li RelationPicker (arama+filtre+sort+sayfalama)
//   - fetchOptions verilirse: klasik sunucu-aramalı SearchableSelect
// `rows` verilirse (config modu) picker kendi sorgusunu ATMAZ, verilen satırları listeler —
// seçenek kümesi çağıran tarafta daraltılmışsa (ör. seçili distribütörün depoları) kullanılır.
//
// #817 `allItems` verilirse (seçilebilecek TÜM adayların {value,label} listesi) picker'ın altına
// "Tümünü Seç / Temizle" şeridi eklenir. İsteğe bağlıdır — verilmezse bileşen eskisi gibi davranır,
// mevcut çağıranların hiçbiri etkilenmez.
import { useState } from 'react';
import { SearchableSelect, RelationPicker } from '../ui';

export function MultiChipSelect({ items = [], onChange, fetchOptions, config, rows, placeholder = 'Ara ve ekle…', disabled = false, pageSize = 20, allItems }) {
  const [resetKey, setResetKey] = useState(0);
  const bulk = !disabled && Array.isArray(allItems) && allItems.length > 0;
  // "Tümü seçili mi" — dizi uzunluğu karşılaştırması yanıltıcı olurdu (listede olmayan, ör. silinmiş
  // bir kayıt seçili kalabilir); adayların hepsi seçim kümesinde mi diye bakılır.
  const selectedValueSet = new Set(items.map((x) => String(x.value)));
  const allSelected = bulk && allItems.every((o) => selectedValueSet.has(String(o.value)));

  // picked: config modunda ham OData satırı, fetchOptions modunda {value,label}
  const add = (value, picked) => {
    if (!value) return;
    // Değer tipi kaynağa göre string/number gelebilir → karşılaştırmaları String'le normalize et.
    const sv = String(value);
    // #281 Zaten seçili bir satıra tekrar tıklamak seçimi KALDIRIR (toggle) — kullanıcı
    // seçimi kaldırmak için chip'teki çarpıyı kullanmak zorunda kalmasın.
    if (items.some((x) => String(x.value) === sv)) {
      onChange(items.filter((x) => String(x.value) !== sv));
      setResetKey((k) => k + 1);
      return;
    }
    const label = config
      ? (picked ? config.getRowLabel(picked) : value)
      : (picked?.label || value);
    onChange([...items, { value, label }]);
    setResetKey((k) => k + 1);
  };

  const remove = (value) => onChange(items.filter((x) => String(x.value) !== String(value)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!disabled && (
        config ? (
          <RelationPicker
            config={config}
            rows={rows}
            value=""
            onChange={add}
            placeholder={placeholder}
            allowClear={false}
            closeOnSelect={false}
            selectedValues={items.map((x) => x.value)}
          />
        ) : (
          <SearchableSelect key={resetKey} value="" onChange={add} fetchOptions={fetchOptions} placeholder={placeholder} pageSize={pageSize} />
        )
      )}
      {bulk && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            // Aday listesinde OLMAYAN seçili değerler (ör. silinmiş kayıt) korunur — "Tümünü Seç"
            // ekleme yapar, kullanıcının istemediği bir çıkarma yapmaz.
            onClick={() => {
              const allValues = new Set(allItems.map((o) => String(o.value)));
              onChange([...items.filter((x) => !allValues.has(String(x.value))), ...allItems]);
            }}
            disabled={allSelected}
            style={bulkBtn(allSelected)}
          >
            Tümünü Seç
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={items.length === 0}
            style={bulkBtn(items.length === 0)}
          >
            Temizle
          </button>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary, #64748b)' }}>
            {items.length} / {allItems.length} seçili
          </span>
        </div>
      )}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {items.map((it) => (
            <span
              key={it.value}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 8px', borderRadius: 999, fontSize: 12,
                background: 'var(--bg-info, #e6f1fb)', color: 'var(--text-info, #185fa5)',
                border: '1px solid rgba(24,95,165,0.2)',
              }}
            >
              {it.label}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(it.value)}
                  aria-label="Kaldır"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontSize: 14, lineHeight: 1, padding: 0 }}
                >×</button>
              )}
            </span>
          ))}
        </div>
      )}
      {items.length === 0 && disabled && (
        <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>—</span>
      )}
    </div>
  );
}

// Toplu seçim düğmesi. `dps-btn` sınıfı BİLEREK kullanılmadı — bu bileşen DetailShell'in
// `.dps` ağacı dışında da kullanılıyor ve orada o sınıf görünmez butona dönüşüyor.
function bulkBtn(off) {
  return {
    padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    border: '1px solid var(--border-color, #cbd5e1)',
    background: 'var(--bg-input, #f6f8fb)', color: 'var(--text-primary, #0f172a)',
    cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1,
  };
}

export default MultiChipSelect;
